import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Alert, Group, Loader, Text } from '@mantine/core';
import {
  AlertTriangle,
  ArrowLeft,
  Clock,
  Maximize2,
  RefreshCw,
  Server,
} from 'lucide-react';
import { Badge, Button, StatusDot } from '../ui';
import { RangeInfoOverlay } from './RangeInfoOverlay';
import { useGuacamoleToken } from '../../hooks/useRanges';
import { useRangeHealth } from '../../hooks/useRangeHealth';
import { useAuthStore } from '../../store/useAuthStore';
import { colors } from '../../theme/tokens';
import type { RangeHealthVm } from '../../api/types';

/**
 * FullPageConsole — ludus2-style immersive console. A full-screen black takeover
 * with a thin toolbar (Back · VM · status · Range Info · Fullscreen · Reconnect)
 * over a full-bleed Guacamole iframe, plus a slide-down Range Info overlay
 * (Machines + Topology). Replaces the cramped modal/embedded pane.
 *
 * Tunnel (Phase B): the token grant now ensures the SSM tunnel server-side, so
 * the iframe paints. If grant returns tunnel_warning, we show a hint; Reconnect
 * re-grants (which re-ensures).
 */
export interface FullPageConsoleProps {
  rangeId: string;
  vmName: string;
  /** Where Back should return to (e.g. `/labs/<id>`). Falls back to the range. */
  returnTo?: string;
}

type ConnState = 'token-loading' | 'connecting' | 'connected' | 'error';

/**
 * Has the Guacamole client inside the iframe drawn anything but black yet?
 *
 * `true`/`false` when we can tell; `null` when we cannot (the iframe is
 * cross-origin, e.g. local dev against the deployed gateway). In production the
 * console is served from the app's own origin (/guacamole on the same host), so
 * the parent can read the guac display's layer canvases. Guac draws decoded
 * image blobs, so the canvases are never tainted.
 *
 * Why this exists: the iframe's `load` event fires when the guac web app's HTML
 * arrives, seconds before the remote desktop paints. The toolbar used to flip to
 * CONNECTED right then, over a black pane. On a cold lab VM the first pixels
 * took 22.8s to arrive (range 4257359509), so trainees took the black screen
 * for a dead console and clicked Back.
 */
function desktopPainted(iframe: HTMLIFrameElement | null): boolean | null {
  let doc: Document | null = null;
  try {
    doc = iframe?.contentDocument ?? null;
  } catch {
    return null;
  }
  if (!doc) return null;
  const layers = Array.from(doc.querySelectorAll<HTMLCanvasElement>('.display canvas')).filter(
    (c) => c.width >= 64 && c.height >= 64,
  );
  if (layers.length === 0) return false;
  const probe = document.createElement('canvas');
  probe.width = 32;
  probe.height = 18;
  const ctx = probe.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  try {
    for (const layer of layers) ctx.drawImage(layer, 0, 0, probe.width, probe.height);
    const px = ctx.getImageData(0, 0, probe.width, probe.height).data;
    let lit = 0;
    for (let i = 0; i < px.length; i += 4) {
      if ((px[i] ?? 0) + (px[i + 1] ?? 0) + (px[i + 2] ?? 0) > 60) lit += 1;
    }
    // The XFCE panel strip (~5% of the screen) arrives a second or two before
    // the wallpaper; measured, a 2% bar flipped to "connected" over a still-
    // black pane. 15% means the desktop itself is on screen.
    return lit > (probe.width * probe.height) * 0.15;
  } catch {
    return null;
  }
}

export function FullPageConsole({ rangeId, vmName, returnTo }: FullPageConsoleProps) {
  const navigate = useNavigate();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const shellRef = useRef<HTMLDivElement | null>(null);
  const [state, setState] = useState<ConnState>('token-loading');
  const [infoOpen, setInfoOpen] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  // F7: a freshly-provisioned Windows box can sit on a black first-boot/RDP
  // screen after the iframe already reports "connected"; surface a dismissible
  // nudge after a short delay so black reads as "booting", not "broken".
  const [bootHint, setBootHint] = useState(false);
  const [hintDismissed, setHintDismissed] = useState(false);
  // null = cannot tell (cross-origin iframe) -> behave as before.
  const [painted, setPainted] = useState<boolean | null>(false);
  const [connectedAt, setConnectedAt] = useState<number | null>(null);

  const { data: token, isPending, isError, error, refetch } = useGuacamoleToken(
    rangeId,
    vmName,
    true,
  );

  // RBAC: trainees see only their assigned VMs in the overlay; instructors all.
  const role = useAuthStore((s) => s.user?.role);
  const ownSub = useAuthStore((s) => s.user?.userId) ?? '';
  const healthQ = useRangeHealth(rangeId);
  const vms: ReadonlyArray<RangeHealthVm> = useMemo(() => {
    const all = healthQ.data?.vms ?? [];
    if (role === 'trainee') return all.filter((v) => v.assignedTo && v.assignedTo === ownSub);
    return all;
  }, [healthQ.data, role, ownSub]);

  useEffect(() => {
    if (isPending) setState('token-loading');
    else if (isError) setState('error');
    else if (token) setState((s) => (s === 'connected' ? s : 'connecting'));
  }, [isPending, isError, token]);

  useEffect(() => {
    if (!token) return;
    const t = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(t);
  }, [token]);

  // Watch for the desktop's first real pixels once the guac app has loaded.
  // Stops as soon as it knows (painted, or undetectable).
  useEffect(() => {
    if (state !== 'connected') {
      setPainted(false);
      setConnectedAt(null);
      return;
    }
    const since = Date.now();
    setConnectedAt(since);
    const tick = () => {
      // Cap the wait: a near-black remote screen must never pin the badge on
      // "starting desktop" forever.
      const p = Date.now() - since > 90_000 ? true : desktopPainted(iframeRef.current);
      setPainted(p);
      return p !== false;
    };
    if (tick()) return;
    const t = window.setInterval(() => {
      if (tick()) window.clearInterval(t);
    }, 750);
    return () => window.clearInterval(t);
  }, [state, token?.guacamoleUrl]);

  // While we wait for pixels, show elapsed time so a slow first start reads
  // as progress rather than a hang.
  const waitingForDesktop = state === 'connected' && painted === false;
  useEffect(() => {
    if (!waitingForDesktop) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [waitingForDesktop]);

  // F7: arm the "still black?" nudge once the iframe connects; reset whenever
  // the VM or connection state changes. When we can SEE the desktop paint, the
  // nudge only appears if it hasn't after 30s; otherwise (cross-origin, can't
  // tell) keep the old 15s timer.
  useEffect(() => {
    setBootHint(false);
    setHintDismissed(false);
    if (state !== 'connected' || painted === true) return;
    const t = window.setTimeout(() => setBootHint(true), painted === null ? 15_000 : 30_000);
    return () => window.clearTimeout(t);
  }, [state, vmName, painted]);

  const reconnect = () => {
    setState('token-loading');
    void refetch();
  };

  const timeToExpiry = token ? Math.max(0, token.expiresAtMs - now) : null;
  const expirySoon = timeToExpiry !== null && timeToExpiry < 60_000;
  const iframeUrl = token?.guacamoleUrl ?? null;

  const goBack = () => {
    // Prefer the explicit origin (Guacamole's iframe pollutes browser history,
    // so router.history.back() would step inside the iframe, not leave the
    // console). Fall back to the range page.
    if (returnTo) {
      void navigate({ to: returnTo });
      return;
    }
    void navigate({ to: '/ranges/$id', params: { id: rangeId } });
  };

  const selectVm = (name: string) => {
    setInfoOpen(false);
    navigate({ to: '/console/$rangeId/$vmName', params: { rangeId, vmName: name } });
  };

  // Give keyboard to Guacamole: focus the iframe ELEMENT *and* its contentWindow
  // (the guac app inside). Focusing only the element leaves keystrokes going to
  // the host page — that was the "can't type" bug. Skip while the Range Info
  // panel is open so host Ctrl+C isn't forwarded into the VM (matches Ludus).
  const focusConsole = () => {
    if (infoOpen) return;
    const el = iframeRef.current;
    try {
      el?.focus({ preventScroll: true });
      el?.contentWindow?.focus();
    } catch {
      /* cross-origin contentWindow guard */
    }
  };

  const toggleInfo = () => setInfoOpen((o) => !o);

  // Return keyboard focus to the Guacamole iframe whenever the Range Info panel
  // CLOSES. This runs off the FRESH infoOpen value — the old approach scheduled
  // focusConsole() from inside toggleInfo, whose closure captured the stale
  // infoOpen===true, so its `if (infoOpen) return` guard fired and the console
  // was left un-typeable after viewing details. Focus the iframe element AND its
  // contentWindow (the guac app) so keystrokes go to the desktop. Matches Ludus.
  useEffect(() => {
    if (infoOpen) return;
    const t = window.setTimeout(() => {
      const el = iframeRef.current;
      try {
        el?.focus({ preventScroll: true });
        el?.contentWindow?.focus();
      } catch {
        /* cross-origin contentWindow guard */
      }
    }, 220);
    return () => window.clearTimeout(t);
  }, [infoOpen]);

  return (
    <div
      ref={shellRef}
      // FORCED-DARK ISLAND. The console is an intentional full-screen black
      // takeover, so every token-driven child (ghost Buttons, `c="dimmed"`,
      // Badge, StatusDot, Alert) must resolve against the DARK palette even when
      // the app is in light mode — otherwise the toolbar controls render
      // near-black-on-near-black and vanish. global.css declares the dark token
      // block on a bare `[data-mantine-color-scheme='dark']` selector precisely
      // so a nested subtree can opt in; Mantine's own component rules use
      // `:where([data-mantine-color-scheme='dark'])` and follow suit.
      data-mantine-color-scheme="dark"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        display: 'flex',
        flexDirection: 'column',
        background: '#000',
      }}
    >
      {/* Toolbar */}
      <Group
        justify="space-between"
        px="sm"
        wrap="nowrap"
        style={{
          height: 44,
          flexShrink: 0, // never collapse onto the iframe (matches Ludus `shrink-0`)
          borderBottom: '1px solid var(--c-bd)',
          background: 'rgb(var(--c-deep))',
        }}
      >
        <Group gap="sm" wrap="nowrap">
          <Button variant="ghost" size="xs" leftIcon={<ArrowLeft size={14} />} onClick={goBack}>
            Back
          </Button>
          <StatusDot
            state={state === 'connected' ? 'running' : state === 'error' ? 'error' : 'running'}
            size="sm"
          />
          <Text ff="JetBrains Mono" size="sm" c="rgb(var(--c-txt-bright))">
            {vmName}
          </Text>
          {token?.protocol && (
            <Badge variant="info" size="xs">
              {token.protocol.toUpperCase()}
            </Badge>
          )}
          <Badge
            variant={
              state === 'connected' && !waitingForDesktop
                ? 'success'
                : state === 'error'
                  ? 'danger'
                  : 'neutral'
            }
            size="xs"
          >
            {waitingForDesktop ? 'starting desktop' : state}
          </Badge>
        </Group>
        <Group gap="xs" wrap="nowrap">
          {timeToExpiry !== null && (
            <Group gap={4}>
              <Clock size={12} color={expirySoon ? colors.hudAmber : 'rgb(var(--c-txt-muted))'} />
              <Text size="xs" c={expirySoon ? colors.hudAmber : 'dimmed'} ff="JetBrains Mono">
                {Math.floor(timeToExpiry / 60_000)}m {Math.floor((timeToExpiry % 60_000) / 1000)}s
              </Text>
            </Group>
          )}
          <Button variant="ghost" size="xs" leftIcon={<Server size={14} />} onClick={toggleInfo}>
            Range Info
          </Button>
          <Button
            variant="ghost"
            size="xs"
            leftIcon={<Maximize2 size={14} />}
            onClick={() => void shellRef.current?.requestFullscreen?.()}
          >
            Fullscreen
          </Button>
          <Button
            variant="ghost"
            size="xs"
            leftIcon={<RefreshCw size={14} />}
            onClick={reconnect}
            disabled={state === 'token-loading'}
          >
            Reconnect
          </Button>
        </Group>
      </Group>

      {token?.tunnelWarning && state !== 'error' && (
        <Alert color="yellow" variant="light" icon={<AlertTriangle size={14} />} radius={0}>
          Tunnel not confirmed — the desktop may take a moment, or click Reconnect.
        </Alert>
      )}

      {/* Console area — focus the guac app on any pointer interaction so typing
          works immediately (matches Ludus). */}
      <div
        style={{ position: 'relative', flex: 1, background: '#000', overflow: 'hidden' }}
        onMouseEnter={focusConsole}
        onMouseDown={focusConsole}
      >
        <RangeInfoOverlay
          open={infoOpen}
          onClose={toggleInfo}
          rangeId={rangeId}
          vms={vms}
          activeVmName={vmName}
          onSelectVm={selectVm}
        />

        {state === 'error' ? (
          <Group justify="center" align="center" style={{ height: '100%' }}>
            <Alert color="red" variant="light" icon={<AlertTriangle size={14} />} maw={520}>
              {(error as Error)?.message ?? 'Failed to grant a console token.'}
            </Alert>
          </Group>
        ) : (
          <>
            {/* Iframe stays mounted while connecting so it can actually load;
                the overlay sits on top until onLoad flips state to connected. */}
            {iframeUrl && (
              <iframe
                ref={iframeRef}
                src={iframeUrl}
                title={`Console: ${vmName}`}
                tabIndex={0}
                style={{ width: '100%', height: '100%', border: 'none', background: '#000' }}
                onLoad={() => {
                  setState('connected');
                  // Focus the guac app so keys go to the desktop right away.
                  window.setTimeout(focusConsole, 200);
                }}
                allow="clipboard-read; clipboard-write; fullscreen"
              />
            )}
            {state !== 'connected' && (
              <div
                style={{
                  position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: 14, background: '#000',
                }}
              >
                <Loader size="sm" color="green" />
                <Text size="sm" c="dimmed">
                  {state === 'token-loading'
                    ? 'Requesting console token…'
                    : 'Establishing tunnel — waiting for the desktop…'}
                </Text>
              </div>
            )}
            {waitingForDesktop && (
              <div
                // Non-blocking: the iframe stays live underneath, so a guac
                // error dialog is still visible and clickable.
                style={{
                  position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: 14,
                  pointerEvents: 'none',
                }}
              >
                <Loader size="sm" color="green" />
                <Text size="sm" c="dimmed">
                  Starting the desktop…
                  {connectedAt !== null && ` ${Math.max(0, Math.round((now - connectedAt) / 1000))}s`}
                </Text>
                <Text size="xs" c="dimmed">
                  The first launch after a lab starts can take up to a minute.
                </Text>
              </div>
            )}
            {bootHint && !hintDismissed && state === 'connected' && !painted && (
              <div
                style={{
                  position: 'absolute', left: '50%', bottom: 24, transform: 'translateX(-50%)',
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                  background: 'rgb(var(--c-card))', border: '1px solid var(--c-bd-bright)',
                  borderRadius: 10, boxShadow: 'var(--shadow-pop)',
                }}
              >
                <Text size="xs" c="rgb(var(--c-txt-bright))">
                  Still a black screen? The desktop may still be booting.
                </Text>
                <Button variant="ghost" size="xs" leftIcon={<RefreshCw size={12} />} onClick={reconnect}>
                  Reconnect
                </Button>
                <Button variant="ghost" size="xs" onClick={() => setHintDismissed(true)}>
                  Dismiss
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
